// 首页与全部记录页共用的任务行操作编排：
// 查看（完成态跳结果页，否则弹进度/缺失资料弹窗）、下载提示、删除确认、
// 补充上传弹窗确认后的完整链路（AddMaterial 上传 → 解析进度弹窗 →
// 案件信息确认表单 → GenerateJudgment 重新生成）
import { ref, onDeactivated, onUnmounted } from "vue";
import { useRouter } from "vue-router";
import { message, Modal } from "ant-design-vue";
import { saveJudgmentDoc, viewingTask, docTypeValue } from "./store";
import {
  deleteJudgmentDoc,
  addJudgmentMaterial,
  getGenerationConfig,
  generationJudgment,
} from "@/api/judicativePaper";

// onRefresh：删除 / 补充材料成功后的回调，各列表页传自己的刷新函数
export function useTaskActions(onRefresh) {
  const router = useRouter();
  // 任务进度/缺失资料弹窗当前展示的任务
  const infoTask = ref(null);
  // 补充上传弹窗当前操作的任务
  const suppTask = ref(null);

  function viewTask(t) {
    if (t.status === "done") {
      // 暂存行数据（接口记录）：结果页以此渲染头部信息，
      // 编辑器内容由 Download 接口按 docId 拉取展示
      viewingTask.value = t;
      router.push({ path: "/home/JudicativePaper/result", query: { id: t.id } });
    } else {
      infoTask.value = t;
    }
  }
  // 下载生成的文书：调 Download 接口取文件流保存到本地（saveJudgmentDoc 共用逻辑）
  async function downloadTask(t) {
    if (!t || t.status !== "done") return;
    try {
      await saveJudgmentDoc(t);
      message.success("文件下载成功");
    } catch (err) {
      // 业务 / 状态码错误 http.js 已 toast；此处兜底网络类静默失败
      console.error("[JudicativePaper] downloadJudgmentDoc error:", err);
      message.error("文书下载失败，请稍后重试");
    }
  }
  // 删除记录：调 Delete 接口按 docId 删除，成功后回调刷新所在页列表
  function deleteTask(t) {
    Modal.confirm({
      title: "删除确认",
      content: `确认删除「${t.name}」这条裁判文书记录吗？`,
      async onOk() {
        try {
          await deleteJudgmentDoc(t.docId ?? t.id);
          message.success("记录已删除");
          onRefresh && onRefresh();
        } catch (err) {
          // 业务 / 状态码错误 http.js 已 toast；此处兜底网络类静默失败
          console.error("[JudicativePaper] deleteJudgmentDoc error:", err);
          message.error("删除失败，请稍后重试");
        }
      },
    });
  }
  // 缺失资料弹窗里的"上传补充材料"
  function openSupplement(t) {
    if (t.status === "done") {
      message.info("已生成完成的裁判文书不支持继续新增材料");
      return;
    }
    suppTask.value = t;
  }
  // 从进度/缺失资料弹窗跳转到补充上传弹窗
  function goSupplementFromInfo(t) {
    infoTask.value = null;
    openSupplement(t);
  }

  // GenerateJudgment 返回 400（缺材料）：构造 missing 态任务，打开 TaskInfoModal，
  // 用户可点「上传补充材料」走 SupplementModal → 重新解析 → 再生成
  function openMissingFromGenerateError({ docId, type, name, errMsg }) {
    const tip =
      errMsg || "当前材料不完整，需补充后重新生成";
    infoTask.value = {
      id: docId,
      docId,
      name: name || "裁判文书",
      type: type || "判决书",
      status: "missing",
      progress: tip,
      missingRequired: [
        {
          name: "缺少必要材料",
          desc: tip,
        },
      ],
      original: [],
      supplements: [],
    };
  }

  // 判断 GenerateJudgment 缺材料错误（不依赖全局 http 扩展字段，按业务文案识别）
  function isGenerateMissingError(err) {
    const msg = String(err?.message || "");
    return /缺少|无法生成|补充材料/.test(msg);
  }
  // ---------- 补充材料 → 重新生成链路（首页 / 记录页共用，由本组合式函数驱动） ----------
  // 补充上传确认后：AddMaterial 上传（进度弹窗「正在上传补充材料…」阶段）→
  // GetGenerationConfig 拉预填配置 → 解析动画 → 案件信息确认表单 → 提交后
  // GenerateJudgment 重新生成（与首页示例文书流程同套路）
  const suppParseActive = ref(false);
  const suppParsePct = ref(26);
  const suppParseDesc = ref("正在上传补充材料…");
  const suppCaseInfoOpen = ref(false);
  const suppConfig = ref(null);
  let suppTimers = [];
  let suppDocId = null;
  // 补充链路沿用原任务文书类型 value，提交 GenerateJudgment 时作为 documentSubType
  let suppDocumentSubType = null;
  let suppTaskType = null;
  let suppTaskName = null;

  // 补充上传弹窗确认：files 为 [{name, size, raw}]，逐个调 AddMaterial 上传，
  // 成功后进入解析动画并弹案件信息表单（见上方链路注释）
  async function onSuppConfirm(files) {
    const t = suppTask.value;
    suppTask.value = null;
    if (!t) return;
    const uploads = files.filter((f) => f.raw);
    if (!uploads.length) return;
    suppDocId = t.docId ?? t.id;
    suppTaskType = t.type || "判决书";
    suppTaskName = t.name || "裁判文书";
    suppDocumentSubType = String(docTypeValue(suppTaskType));
    suppParseActive.value = true;
    suppParsePct.value = 26;
    suppParseDesc.value = "正在上传补充材料…";
    const startedAt = Date.now();
    try {
      await Promise.all(
        uploads.map((f) => addJudgmentMaterial(suppDocId, f.raw)),
      );
      // 路由已切走（onDeactivated 已收起弹窗并复位）：终止流程
      if (!suppParseActive.value) return;
      // 上传成功即刷新列表：行内状态随补充材料流转，后续取消案件信息表单
      // 也不至于停留在旧状态（onRefresh(true) 为静默刷新，无参刷新不受影响）
      onRefresh && onRefresh(true);
      // 拉取生成配置预填案件信息表单；失败不阻断：表单回退默认预填值
      try {
        const cfgRes = await getGenerationConfig(suppDocId);
        suppConfig.value = (cfgRes && (cfgRes.data || cfgRes)) || null;
      } catch (err) {
        console.error("[JudicativePaper] getGenerationConfig error:", err);
        suppConfig.value = null;
      }
      if (!suppParseActive.value) return;
      // 解析动画：整体节奏不短于确认后 1.8s（与首页 startParsing 一致）
      suppParseDesc.value = "正在解压案件材料";
      const rest = Math.max(0, 1800 - (Date.now() - startedAt));
      suppTimers.push(
        setTimeout(() => (suppParsePct.value = 62), Math.min(650, rest)),
      );
      suppTimers.push(
        setTimeout(() => {
          suppParseActive.value = false;
          suppCaseInfoOpen.value = true;
        }, rest),
      );
    } catch (err) {
      // 上传失败：收起进度弹窗。业务 / 状态码错误 http.js 已 toast；
      // 此处兜底网络类静默失败
      suppParseActive.value = false;
      console.error("[JudicativePaper] addJudgmentMaterial error:", err);
      message.error("补充材料上传失败，请稍后重试");
    }
  }

  // 案件信息表单「提交生成」：对补充过材料的文书重新生成——
  // 提交后不等结果先刷新列表（行内「生成中」），请求 settle 时后端已把最终
  // 状态落库，再静默刷新一次让行内状态闭环（与首页 submitCaseGenerate 同套路）
  function submitSuppGenerate(payload) {
    suppCaseInfoOpen.value = false;
    if (!suppDocId) {
      message.error("缺少文书标识，请重新补充材料");
      return;
    }
    message.success("已提交文书生成");
    // 行已在列表中：静默原地刷新，行内状态切「生成中」即可
    onRefresh && onRefresh(true);
    generationJudgment({
      docId: suppDocId,
      mode: payload.mode,
      caseReason: payload.caseReason,
      applicableProcedure: payload.applicableProcedure,
      caseType: payload.caseType,
      documentSubType: suppDocumentSubType,
    })
      .then(() => {
        message.success("文书生成完成");
      })
      .catch((err) => {
        console.error("[JudicativePaper] generationJudgment error:", err);
        if (isGenerateMissingError(err)) {
          // 全局拦截器已 toast 过，再弹缺材料窗引导补充
          openMissingFromGenerateError({
            docId: suppDocId,
            type: suppTaskType || "判决书",
            name: suppTaskName || "裁判文书",
            errMsg: err?.message,
          });
          return;
        }
        // 业务 400 已由 http 拦截器 toast；此处仅兜底无有效文案的网络类失败
        const msg = err?.message;
        if (!msg || msg === "undefined" || msg === "Error") {
          message.error("文书生成提交失败，请稍后重试");
        }
      })
      .finally(() => {
        // 成功 / 失败后端均已落库最终状态：静默刷新列表，行内状态更新为最终态
        onRefresh && onRefresh(true);
      });
  }

  // 路由切走（keep-alive 停用）/ 组件卸载：终止补充流程的解析动画并收起弹窗
  function resetSuppFlow() {
    suppTimers.forEach(clearTimeout);
    suppTimers = [];
    suppParseActive.value = false;
    suppCaseInfoOpen.value = false;
  }
  onDeactivated(resetSuppFlow);
  onUnmounted(resetSuppFlow);

  return {
    infoTask,
    suppTask,
    suppParseActive,
    suppParsePct,
    suppParseDesc,
    suppCaseInfoOpen,
    suppConfig,
    viewTask,
    downloadTask,
    deleteTask,
    openSupplement,
    goSupplementFromInfo,
    openMissingFromGenerateError,
    isGenerateMissingError,
    onSuppConfirm,
    submitSuppGenerate,
  };
}
